import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2, Tag } from "lucide-react";

interface HabitCategory {
  id: string;
  name: string;
}

interface Habit {
  id: string;
  habit_category_id: string;
  name: string;
}

interface PathHabitsEditorProps {
  pathId: string | undefined;
  disabled: boolean;
}

export function PathHabitsEditor({ pathId, disabled }: PathHabitsEditorProps) {
  const { user } = useAuth();
  const [categories, setCategories] = useState<HabitCategory[]>([]);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const addInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const fetchData = useCallback(async () => {
    if (!pathId) return;
    setLoading(true);
    const [catRes, habRes] = await Promise.all([
      supabase.from("habit_category").select("id, name").order("name"),
      supabase.from("habit").select("id, habit_category_id, name").eq("path_id", pathId).order("name"),
    ]);
    if (catRes.error) toast.error("Erreur chargement thématiques");
    if (habRes.error) toast.error("Erreur chargement habitudes");
    setCategories(catRes.data || []);
    setHabits(habRes.data || []);
    setLoading(false);
  }, [pathId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async (categoryId: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const { data, error } = await supabase.from("habit").insert({
      habit_category_id: categoryId,
      path_id: pathId!,
      name: trimmed,
      created_by: user?.id,
      updated_by: user?.id,
    }).select("id, habit_category_id, name").single();
    if (error) {
      toast.error("Erreur lors de l'ajout");
      return;
    }
    if (data) {
      setHabits((prev) => [...prev, data]);
    }
    setNewName("");
    // Keep addingTo open so user can chain entries
    setTimeout(() => addInputRef.current?.focus(), 0);
  };

  const handleAddBlur = (categoryId: string) => {
    // Delay to allow button clicks to fire first
    setTimeout(() => {
      const trimmed = newName.trim();
      if (!trimmed) {
        setAddingTo(null);
        setNewName("");
      }
      // If there's text, don't auto-close — user might click elsewhere intentionally
      // but we save and close
      if (trimmed && addingTo === categoryId) {
        handleAdd(categoryId).then(() => {
          setAddingTo(null);
        });
      }
    }, 150);
  };

  const handleDelete = async (habitId: string) => {
    const { error } = await supabase.from("habit").delete().eq("id", habitId);
    if (error) {
      toast.error("Erreur lors de la suppression");
      return;
    }
    setHabits((prev) => prev.filter((h) => h.id !== habitId));
  };

  const handleEditSave = async (habitId: string) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    const { error } = await supabase
      .from("habit")
      .update({ name: trimmed, updated_by: user?.id })
      .eq("id", habitId);
    if (error) {
      toast.error("Erreur lors de la mise à jour");
      return;
    }
    setHabits((prev) => prev.map((h) => (h.id === habitId ? { ...h, name: trimmed } : h)));
    setEditingId(null);
  };

  const handleEditBlur = (habitId: string) => {
    setTimeout(() => {
      if (editingId === habitId) {
        const trimmed = editName.trim();
        if (trimmed) {
          handleEditSave(habitId);
        } else {
          setEditingId(null);
        }
      }
    }, 150);
  };

  if (!pathId) {
    return (
      <p className="text-sm text-muted-foreground">
        Sauvegardez le parcours avant d'ajouter des habitudes.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Aucune thématique d'habitudes. Créez-en dans Paramètres → Thématiques d'habitudes.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {categories.map((cat) => {
        const catHabits = habits
          .filter((h) => h.habit_category_id === cat.id)
          .sort((a, b) => a.name.localeCompare(b.name, "fr"));

        return (
          <div
            key={cat.id}
            className="rounded-lg border border-border/60 bg-card overflow-hidden"
          >
            {/* Category header */}
            <div className="flex items-center justify-between px-3 py-2.5 bg-muted/40 border-b border-border/40">
              <div className="flex items-center gap-2">
                <Tag className="h-3.5 w-3.5 text-primary" />
                <span className="text-sm font-medium">{cat.name}</span>
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
                  {catHabits.length}
                </Badge>
              </div>
              {!disabled && addingTo !== cat.id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setAddingTo(cat.id);
                    setNewName("");
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Ajouter
                </Button>
              )}
            </div>

            {/* Habits list */}
            <div className="px-3 py-1.5">
              {catHabits.length === 0 && addingTo !== cat.id && (
                <p className="text-xs text-muted-foreground py-2 text-center">Aucune habitude</p>
              )}

              {catHabits.map((habit) => (
                <div key={habit.id} className="flex items-center gap-2 py-1 group">
                  {editingId === habit.id ? (
                    <Input
                      ref={editInputRef}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-7 text-sm flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleEditSave(habit.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={() => handleEditBlur(habit.id)}
                      autoFocus
                    />
                  ) : (
                    <>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span
                        className={`text-sm flex-1 ${!disabled ? "cursor-pointer hover:text-primary transition-colors" : ""}`}
                        onClick={() => {
                          if (disabled) return;
                          setEditingId(habit.id);
                          setEditName(habit.name);
                        }}
                      >
                        {habit.name}
                      </span>
                      {!disabled && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive"
                          onClick={() => handleDelete(habit.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              ))}

              {addingTo === cat.id && (
                <div className="flex items-center gap-2 py-1">
                  <span className="text-xs text-muted-foreground">•</span>
                  <Input
                    ref={addInputRef}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Nouvelle habitude…"
                    className="h-7 text-sm flex-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAdd(cat.id);
                      if (e.key === "Escape") {
                        setAddingTo(null);
                        setNewName("");
                      }
                    }}
                    onBlur={() => handleAddBlur(cat.id)}
                    autoFocus
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
